import { HTMLAttributes, ReactNode, useEffect, useState } from 'react';
import { cn } from '../../../lib/utils';
import { registerModalBackHandler } from '../../../lib/native';
import { Logo } from './Logo';
import { NavItem } from './NavItem';

export interface SidebarNavItem {
  /** Stable id used to match `active`. Falls back to `label` when it's a string. */
  key?: string;
  /** Tabler icon name (without `ti-` prefix) or any icon node (inline SVG). */
  icon?: string | ReactNode;
  label: ReactNode;
  href?: string;
  /** Trailing --accent notification dot. */
  dot?: boolean;
  /** Trailing gold count pill (e.g. unread count). Hidden when 0/empty. */
  badge?: string | number;
  onClick?: () => void;
}

export interface SidebarSection {
  /** Uppercase overline label above the group. Omit for an unlabelled group. */
  label?: string;
  items: SidebarNavItem[];
  /** Header toggles the group open/closed (persisted per label in
   *  localStorage). Requires `label` — a collapsible unlabelled group has
   *  nothing to click. Off by default so existing callers (traveler/host
   *  nav) render exactly as before. */
  collapsible?: boolean;
}

export interface SidebarTrip {
  /** Optional quiet overline above the title (legacy card). Omit + set `dot` for the status card. */
  label?: string;
  /** Serif title, e.g. "Solo Signature Lisbon Expedition". */
  title: string;
  /** Meta line, e.g. "Confirmed · Jun 4–7". */
  meta?: string;
  /** Status dot colour before `meta` — switches to the trip status card layout. */
  dot?: 'success' | 'gold' | 'violet' | 'crimson';
}

export interface SidebarDetailRow {
  /** Tabler icon name (without `ti-` prefix). */
  icon?: string;
  label: string;
  value: string;
}

export interface SidebarUser {
  name: string;
  /** Quiet second line, e.g. "Traveler". */
  subtitle?: string;
  /** Avatar node (e.g. <Avatar size={30}/>). */
  avatar?: ReactNode;
  onClick?: () => void;
}

const DOT_COLORS: Record<string, string> = {
  success: 'rgb(var(--success))',
  gold: 'rgb(var(--gold))',
  violet: 'rgb(var(--violet))',
  crimson: 'rgb(var(--crimson))',
};

/**
 * Sidebar — the app shell rail. Recessed --sidebar-bg ground, wordmark at top,
 * an optional "current trip" card, a primary nav group (flex-grows to push the
 * footer down), and an optional secondary/footer nav group above the fold.
 *
 * Two usage modes:
 *  - Static rail (default): a plain desktop `<aside>` of `width` px.
 *  - App shell mode: pass `open`/`onClose` and it becomes a responsive shell —
 *    a slide-in drawer with backdrop on mobile (with safe-area padding for
 *    notched devices) and a sticky, optionally collapsible rail on md+.
 *
 * items / sections / footerItems are arrays of:
 *   { key, icon, label, href?, dot?, onClick? }
 * The entry whose `key` matches `active` is highlighted. Prefer `onClick`
 * with a router `navigate()` in SPA contexts — `href` renders a plain <a>
 * which triggers a full page load.
 *
 * `headerActions` renders quiet icon buttons beside the logo (theme toggle,
 * collapse). `headerSlot` renders below the logo (e.g. a trip switcher card);
 * `footerSlot` renders pinned at the very bottom (e.g. an account menu) —
 * or pass `user` for the standard user footer card. `groupLabel` is the
 * sentence-case label above the primary nav ("Your trip"); `details` renders
 * the collapsible key/value rows ("Trip details"). `children` renders inside
 * the nav column after items/sections.
 */
export interface SidebarProps extends HTMLAttributes<HTMLElement> {
  items?: SidebarNavItem[];
  /** Grouped nav with uppercase section labels. Rendered after `items`. */
  sections?: SidebarSection[];
  footerItems?: SidebarNavItem[];
  /** key (or string label) of the active item. */
  active?: string;
  trip?: SidebarTrip;
  /** Custom block under the logo (e.g. trip switcher). */
  headerSlot?: ReactNode;
  /** Quiet icon actions beside the logo (e.g. IconButtons). */
  headerActions?: ReactNode;
  /** Sentence-case group label above the primary nav, e.g. "Your trip". */
  groupLabel?: string;
  /** Rows inside the collapsible details section. */
  details?: SidebarDetailRow[];
  /** Toggle label for the details section. Default "Trip details". */
  detailsLabel?: string;
  /** Details section starts open. Default false. */
  detailsDefaultOpen?: boolean;
  /** User footer card below the footer nav (ignored when footerSlot is set). */
  user?: SidebarUser;
  /** Node pinned below the footer nav (e.g. an account menu). */
  footerSlot?: ReactNode;
  /** Rail width in px — static mode only. Default 252. */
  width?: number;
  /** Wordmark height in px. Default 22. */
  logoHeight?: number;
  /** Fired with the item key/label on click. */
  onNavigate?: (key: string) => void;
  /** Extra blocks rendered below the primary nav. */
  children?: ReactNode;
  /** Shell mode: mobile drawer open state. Enables responsive shell rendering. */
  open?: boolean;
  /** Shell mode: close the mobile drawer (backdrop tap / after navigating). */
  onClose?: () => void;
  /** Shell mode: desktop collapsed (icon-only) state. */
  collapsed?: boolean;
  /** Shell mode: toggle desktop collapse. Renders the collapse/expand buttons. */
  onToggleCollapse?: () => void;
  /** Makes the wordmark clickable. */
  onLogoClick?: () => void;
}

const COLLAPSE_ICON = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
    <path fillRule="evenodd" d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zM2 10a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10zm0 5.25a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75a.75.75 0 01-.75-.75z" clipRule="evenodd" />
  </svg>
);

export function Sidebar({
  items = [],
  sections = [],
  footerItems = [],
  active,
  trip,
  headerSlot,
  headerActions,
  groupLabel,
  details,
  detailsLabel = 'Trip details',
  detailsDefaultOpen = false,
  user,
  footerSlot,
  width = 252,
  logoHeight = 22,
  onNavigate,
  children,
  open,
  onClose,
  collapsed,
  onToggleCollapse,
  onLogoClick,
  className,
  style,
  ...props
}: SidebarProps) {
  // Shell mode: responsive drawer/rail driven by Tailwind classes.
  const shell = open !== undefined || onClose !== undefined;
  const collapsible = !!onToggleCollapse;
  const c = collapsed ?? false;
  const [detailsOpen, setDetailsOpen] = useState(detailsDefaultOpen);

  // Per-section collapse (opt-in via section.collapsible), persisted per
  // label so it survives reloads/navigation. Lazy-init reads localStorage
  // once rather than defaulting every section open and flashing closed a
  // moment later.
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>(() => {
    if (typeof window === 'undefined') return {};
    const out: Record<string, boolean> = {};
    for (const section of sections) {
      if (section.collapsible && section.label) {
        out[section.label] = window.localStorage.getItem(`sk_sidebar_section_${section.label}`) === '1';
      }
    }
    return out;
  });

  function toggleSection(label: string) {
    setCollapsedSections((prev) => {
      const next = { ...prev, [label]: !prev[label] };
      window.localStorage.setItem(`sk_sidebar_section_${label}`, next[label] ? '1' : '0');
      return next;
    });
  }

  const itemKey = (it: SidebarNavItem) => it.key ?? (typeof it.label === 'string' ? it.label : '');

  // Landing on a page inside a collapsed section opens that section so the
  // current page is visible — once. After that the user's toggle is respected,
  // even while the section holds the active page (it is not forced open). The
  // stored preference is left alone, so the next visit elsewhere is unchanged.
  useEffect(() => {
    if (active == null) return;
    setCollapsedSections((prev) => {
      let next = prev;
      for (const section of sections) {
        if (section.label && prev[section.label] && section.items.some((it) => itemKey(it) === active)) {
          next = { ...next, [section.label]: false };
        }
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Android hardware back button closes the mobile nav drawer instead of
  // navigating the route underneath it.
  useEffect(() => {
    if (!shell || !open || !onClose) return;
    return registerModalBackHandler(onClose);
  }, [shell, open, onClose]);

  const renderItem = (it: SidebarNavItem, size: 'md' | 'sm') => {
    const key = itemKey(it);
    const labelText = typeof it.label === 'string' ? it.label : key;
    return (
      <NavItem
        key={key}
        icon={it.icon}
        href={it.href}
        size={size}
        dot={it.dot && !(shell && c)}
        badge={shell && c ? undefined : it.badge}
        active={active != null && key === active}
        title={shell && c ? labelText : undefined}
        className={shell && c ? 'md:justify-center' : undefined}
        label={<span className={shell && c ? 'md:hidden' : undefined}>{it.label}</span>}
        onClick={() => {
          it.onClick?.();
          onNavigate?.(key);
        }}
      />
    );
  };

  // Sentence case, no letter-spacing — the style guide forbids uppercase labels.
  const sectionLabel = (text: string) => (
    <p
      className={cn('text-caption font-medium px-2.5 mb-2 mt-4 first:mt-0', shell && c ? 'md:hidden' : '')}
      style={{ color: 'var(--text-dim)' }}
    >
      {text}
    </p>
  );

  const sectionHeaderButton = (label: string, open: boolean) => (
    <button
      type="button"
      onClick={() => toggleSection(label)}
      aria-expanded={open}
      className={cn('sk-hit', 'flex items-center justify-between w-full px-2.5 mb-2 mt-4 first:mt-0', shell && c ? 'md:hidden' : '')}
      style={{ background: 'none', border: 'none', cursor: 'pointer' }}
    >
      <span className="text-caption font-medium" style={{ color: 'var(--text-dim)' }}>{label}</span>
      <i
        className="ti ti-chevron-down"
        style={{ color: 'var(--text-dim)', fontSize: 'var(--fs-caption)', transition: 'transform .2s ease', transform: open ? 'none' : 'rotate(-90deg)' }}
      />
    </button>
  );

  const renderSection = (section: SidebarSection, i: number, size: 'md' | 'sm') => {
    const isCollapsible = !!(section.collapsible && section.label);
    const open = !isCollapsible || !collapsedSections[section.label!];
    return (
      <div key={section.label ?? i} className="flex flex-col gap-0.5">
        {isCollapsible
          ? sectionHeaderButton(section.label!, open)
          : (section.label ? sectionLabel(section.label) : null)}
        {open && section.items.map((it) => renderItem(it, size))}
      </div>
    );
  };

  const tripCard = trip ? (
    trip.dot ? (
      // Trip status card — serif title, status dot + meta (per the traveler screens).
      <div
        className={shell && c ? 'md:hidden' : undefined}
        style={{ background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 10, padding: '11px 13px', marginBottom: 20 }}
      >
        <div style={{ fontFamily: 'var(--font-serif)', fontSize: 'var(--fs-footnote)', color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {trip.title}
        </div>
        {trip.meta ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 7 }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: DOT_COLORS[trip.dot], flexShrink: 0 }} />
            <span style={{ fontFamily: 'var(--font-sans)', color: 'var(--text-soft)', fontSize: 'var(--fs-caption)' }}>{trip.meta}</span>
          </div>
        ) : null}
      </div>
    ) : (
      // Legacy overline card.
      <div
        className={shell && c ? 'md:hidden' : undefined}
        style={{ background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 'var(--r-sm)', padding: '11px 13px', marginBottom: 20 }}
      >
        <div style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-caption2)', color: 'var(--text-dim)', marginBottom: 4 }}>
          {trip.label ?? 'Current trip'}
        </div>
        <div style={{ fontFamily: 'var(--font-serif)', fontSize: 'var(--fs-subhead)', color: 'var(--text)', lineHeight: 1.2 }}>
          {trip.title}
        </div>
        {trip.meta ? (
          <div style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-caption2)', color: 'var(--text-dim)', marginTop: 4 }}>{trip.meta}</div>
        ) : null}
      </div>
    )
  ) : null;

  const groupLabelNode = groupLabel ? (
    <div
      className={shell && c ? 'md:hidden' : undefined}
      style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-caption)', color: 'var(--text-dim)', fontWeight: 500, margin: '2px 6px 8px' }}
    >
      {groupLabel}
    </div>
  ) : null;

  const detailsNode = details && details.length > 0 ? (
    <div className={shell && c ? 'md:hidden' : undefined}>
      <button
        type="button"
        onClick={() => setDetailsOpen((o) => !o)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
          background: 'transparent', border: 'none', cursor: 'pointer', padding: '14px 6px 9px', marginTop: 6,
        }}
      >
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-caption)', color: 'var(--text-dim)', fontWeight: 500 }}>{detailsLabel}</span>
        <i
          className="ti ti-chevron-down"
          style={{ color: 'var(--text-dim)', fontSize: 'var(--fs-footnote)', transition: 'transform .2s ease', transform: detailsOpen ? 'rotate(180deg)' : 'none' }}
        />
      </button>
      {detailsOpen ? (
        <div style={{ padding: '0 8px 6px', fontFamily: 'var(--font-sans)' }}>
          {details.map((row, i) => (
            <div
              key={row.label}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0',
                borderBottom: i < details.length - 1 ? '1px solid var(--line)' : 'none',
              }}
            >
              {row.icon ? <i className={`ti ti-${row.icon}`} style={{ color: 'var(--text-dim)', fontSize: 'calc(12px * var(--text-scale))' }} /> : null}
              <span style={{ color: 'var(--text-dim)', fontSize: 'var(--fs-caption)', flex: 1 }}>{row.label}</span>
              <span style={{ color: 'var(--text)', fontSize: 'var(--fs-caption)' }}>{row.value}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  ) : null;

  const userFooter = footerSlot ?? (user ? (
    <div
      role={user.onClick ? 'button' : undefined}
      tabIndex={user.onClick ? 0 : undefined}
      onClick={user.onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 9, fontFamily: 'var(--font-sans)',
        cursor: user.onClick ? 'pointer' : 'default',
      }}
    >
      {user.avatar}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 'var(--fs-footnote)', color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.name}</div>
        {user.subtitle ? <div style={{ fontSize: 'var(--fs-caption2)', color: 'var(--text-dim)' }}>{user.subtitle}</div> : null}
      </div>
    </div>
  ) : null);

  const logo = <Logo variant={shell && c ? 'mark' : 'full'} height={logoHeight} />;

  if (!shell) {
    // Static rail — original design-system rendering.
    return (
      <aside
        className={className}
        style={{
          width,
          flex: 'none',
          background: 'var(--sidebar-bg)',
          borderRight: '1px solid var(--line)',
          display: 'flex',
          flexDirection: 'column',
          padding: 'var(--sp-sidebar-pad-y) var(--sp-sidebar-pad-x)',
          overflowY: 'auto',
          ...style,
        }}
        {...props}
      >
        <div style={{ marginBottom: 24, padding: '0 2px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {onLogoClick ? (
            <button className="sk-hit" onClick={onLogoClick} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>{logo}</button>
          ) : logo}
          {headerActions ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 1 }}>{headerActions}</div>
          ) : null}
        </div>

        {headerSlot}
        {tripCard}
        {groupLabelNode}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
          {items.map((it) => renderItem(it, 'md'))}
          {sections.map((section, i) => renderSection(section, i, 'md'))}
          {detailsNode}
          {children}
        </div>

        {footerItems.length ? (
          <div style={{ borderTop: '1px solid var(--line)', marginTop: 16, paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {footerItems.map((it) => renderItem(it, 'sm'))}
          </div>
        ) : null}
        {userFooter ? (
          <div style={{ paddingTop: 14, marginTop: 14, borderTop: '1px solid var(--line)' }}>{userFooter}</div>
        ) : null}
      </aside>
    );
  }

  // App shell mode — mobile drawer + sticky/collapsible desktop rail.
  return (
    <>
      {open && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={onClose} />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-60 flex flex-col transition-all duration-300 select-none pl-safe',
          'md:sticky md:top-0 md:h-dvh md:translate-x-0 md:z-auto',
          c ? 'md:w-16' : 'md:w-60',
          open ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
          className,
        )}
        style={{ background: 'var(--sidebar-bg)', borderRight: '1px solid var(--line)', ...style }}
        {...props}
      >
        {/* Logo row */}
        <div
          className={cn('px-5 pb-4 pt-safe-or-3 flex items-center gap-2', c ? 'md:px-0 md:justify-center' : (collapsible || headerActions) ? 'justify-between' : '')}
          style={{ borderBottom: '1px solid var(--line)' }}
        >
          {onLogoClick ? (
            <button onClick={onLogoClick} className="flex items-center gap-2 hover:opacity-80 transition-opacity min-w-0 sk-hit">
              {logo}
            </button>
          ) : (
            <span className="flex items-center gap-2 min-w-0">{logo}</span>
          )}
          {(headerActions || (collapsible && !c)) && (
            <span className={cn('flex items-center gap-0.5 shrink-0', c ? 'md:hidden' : '')}>
              {headerActions}
              {collapsible && !c && (
                <button
                  onClick={onToggleCollapse}
                  aria-label="Collapse sidebar"
                  title="Collapse"
                  className="hidden md:flex items-center justify-center w-7 h-7 rounded-lg hover:bg-black/5 transition-colors shrink-0"
                  style={{ color: 'var(--text-dim)' }}
                >
                  {COLLAPSE_ICON}
                </button>
              )}
            </span>
          )}
        </div>

        {/* Expand button when collapsed */}
        {collapsible && c && (
          <button
            onClick={onToggleCollapse}
            aria-label="Expand sidebar"
            title="Expand"
            className="hidden md:flex items-center justify-center mx-auto mt-2 w-7 h-7 rounded-lg hover:bg-black/5 transition-colors"
            style={{ color: 'var(--text-dim)' }}
          >
            {COLLAPSE_ICON}
          </button>
        )}

        {/* Nav column — the only scrolling region; logo and footer stay pinned */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 flex flex-col gap-0.5">
          {headerSlot}
          {tripCard}
          {groupLabelNode}

          {items.map((it) => renderItem(it, 'md'))}
          {sections.map((section, i) => renderSection(section, i, 'md'))}
          {detailsNode}
          {children}

          {footerItems.length ? (
            <div className="mt-auto pt-4 flex flex-col gap-0.5" style={{ borderTop: '1px solid var(--line)' }}>
              {footerItems.map((it) => renderItem(it, 'sm'))}
            </div>
          ) : null}
        </nav>

        {/* Pinned footer (e.g. account menu / user card) */}
        {userFooter ? (
          <div className="px-3 pt-3 pb-safe-or-4" style={{ borderTop: '1px solid var(--line)' }}>
            {userFooter}
          </div>
        ) : null}
      </aside>
    </>
  );
}

export default Sidebar;
